import { Entity, EntityLink } from '../entity';
import { ActivityEntityName } from './activity.entity.name';
import { ActivityShareType, ActivitySource, Shareable } from './activity.sharing';
import { BackendNotificationMessage } from '../sockets/backend.notification.message';
import { ActivityKind } from './activity.type';
import { TaskKind } from '../tasks/task.kind';
import { DateUtility } from '../../utilities/date.utility';


export type AttachmentType = 'File' | 'Image' | 'Video' | 'Form' | 'Signature';

export type AttachmentKind = ActivityKind | TaskKind;;

export interface Attachment {
    _id?: string;
    parentId?: string;
    entityId?: string;
    entityName?: string | ActivityEntityName;
    kind?: AttachmentKind;
    type?: AttachmentType;
    title?: string;
    description?: string;
    content?: string;
    path?: string;
    data?: any;
    fileName?: string;
    createdDate?: Date;
    createdBy?: string;
    thumbnail?: string;
    integration?: AttachmentIntegration;
    claimReference?: Entity;
    tags?: Entity[];

    /** This is a temporary flag to indicate an attachment is syncing  */
    syncing?: boolean;

    updatedAt?: Date;
}

export interface MetadataAttachment extends Attachment {
    sensitive?: boolean;
    shared?: ActivityShareType[];
    systemSharing?: boolean;

    effectiveSharing?: Shareable;
    // This field to keep the calculated effectiveSharing from server. it is like the backup to keep the original data.
    // when we update the main file (main file is the original file, we create new other attachments from this file by pick related file)
    // after we update the file, if there is any changes for the sharing, we can use this field to update the effectiveSharing to UI
    originalEffectiveSharing?: Shareable;

    metadata?: Entity[];
    isFile?: boolean;
    /** Copied from the source activity's `readOnly`: an integration-owned file users may not delete, re-tag or re-share. Absent means false. */
    readOnly?: boolean;
    source?: ActivitySource

    entityId?: string;
    entity?: ActivityEntityName;

    /** Tasks / SOR items the row belongs to (Files board "Task / SOR" grouping; several when one photo was added to many). Unset for other sources. */
    taskItems?: Entity[];
}

export interface AttachmentIntegration {
    _id: string;
    error: string;
}

export interface File extends Entity {
    attachment: Attachment;
    links: EntityLink[];
    tags: Entity[];
    createdBy: Entity;
    createdAt: Date;
}

export interface TagCount {
    tag: Entity;
    count: number;
}

export interface FileDashboardResponse {
    tags: TagCount[];
    files: File[];
}

export interface AttachmentBlobCopyRequest {

    /** This is the id of the attachment to copy */
    _id: string;

    /** This is the id of the parent entity that owns the attachment */
    parentId: string;

    /** This is the type of the parent entity */
    parentType: 'Activity' | 'Task';

    /** This is the name of the container to copy the blob to */
    destinationContainer?: string;

    /** This is the name of the blob in the destination container, by default it will be the same as the source */
    fileName?: string;

    /** This is the name of the tenant that owns the original blob */
    notification?: BackendNotificationMessage;
}

export interface AttachmentBlobExternalCopyRequest {
    /** This is the id of the attachment to copy */
    _id: string;

    /** This is the id of the parent entity that owns the attachment */
    parentId: string;

    /** This is the type of the parent entity */
    parentType: 'Activity';

    /** This is the source url of the attachment */
    sourceUrl: string;

    /** The file name to be copied, helps determine destination */
    fileName: string;
}

export function getExtension(url: string) {
    const parts = url.split('.');
    return parts[parts.length - 1];
}

export function getFilename(url: string) {
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    return filename;
}

export function getFilenameWithoutExtension(url: string) {
    const filename = getFilename(url);
    const extension = getExtension(filename);
    return filename.replace(`.${extension}`, '').trim();

    // const parts = url.split('/');
    // const filename = parts[parts.length - 1];
    // const extension = getExtension(filename);
    // return filename.replace(`.${extension}`, '').trim();
}

export function getExtensionFromMimeType(mimeType: string) {
    const imageMimeToFileExtension = {
        'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/gif': 'gif',
        'image/webp': 'webp', 'image/tiff': 'tif', 'image/bmp': 'bmp', 'image/x-icon': 'ico',
        'image/svg+xml': 'svg', 'image/heif': 'heif', 'image/heic': 'heic', 'image/avif': 'avif'
    };
    const extension = imageMimeToFileExtension[mimeType] ?? '';
    return extension;
}

export function getFileThumbnailUrl(attachment: Attachment, cacheBust = false): string {

    if (attachment.type !== 'File') {
        if (attachment.type !== 'Form') {
            return attachment.thumbnail;
        }

        if (!attachment.path) {
            return attachment.thumbnail;
        }
    }

    const path = attachment.path;
    const fileName = getFilenameWithoutExtension(path);
    const baseUrl = path.substring(0, path.indexOf('/files')) + '/thumbnails';
    let cacheBuster = '';
    if (cacheBust && attachment.updatedAt) {
        cacheBuster = `?v=${DateUtility.getDateTime(attachment.updatedAt).toISO()}`;
    }

    return `${baseUrl}/${fileName}.webp${cacheBuster}`;

}

export function enableCacheBurster(attachment: Attachment): boolean {
    const useCacheBurster = attachment.kind === ActivityKind.Form || (attachment.entityName && [ActivityEntityName.Invoice, ActivityEntityName.QuotePro].includes(attachment.entityName as ActivityEntityName));
    return useCacheBurster;
}